/* istanbul ignore file */
import { useCallback, useState, useEffect, useRef } from 'react';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { useTimeout } from 'site-modules/shared/hooks/use-timeout';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { CANVAS_CREATIVE_ID } from 'site-modules/canvas/constants/canvas';

export function useDockResize(iframeRef) {
  const [isDocked, toggleDocked] = useToggle(false);
  const [showContent, setShowContent] = useState(true);
  const [setCollapseTimeout] = useTimeout();

  const containerRef = useRef();
  const resizeRef = useRef();
  const parentBoundingClientRect = useRef();
  const beforeDockWidth = useRef();
  const isResizingWindow = useRef();
  const onResizeEndRef = useRef();

  const getIframeParent = useCallback(() => iframeRef?.current?.parentElement, [iframeRef]);

  const setWidth = useCallback((element, width) => {
    element?.style.setProperty('width', `${parseInt(width, 10)}px`, 'important');
  }, []);

  const hideIframe = useCallback(
    callback => {
      const iframeParent = getIframeParent();
      iframeParent?.style.setProperty('opacity', '0');
      setCollapseTimeout(callback, 160);
    },
    [getIframeParent, setCollapseTimeout]
  );

  const showIframe = useCallback(() => {
    const iframeParent = getIframeParent();
    requestAnimationFrame(() => {
      iframeRef.current?.contentWindow.requestAnimationFrame(() => {
        iframeParent?.style.removeProperty('opacity');
      });
    });
  }, [getIframeParent, iframeRef]);

  const applyStyles = useCallback(() => {
    const iframeParent = getIframeParent();
    setWidth(resizeRef.current, window?.getComputedStyle(containerRef?.current)?.width);
    containerRef.current?.classList.toggle('resizing', true);
    iframeParent?.style.setProperty('pointer-events', 'none');
    document.body.style.setProperty('cursor', 'col-resize');
    document.body.style.setProperty('user-select', 'none');
  }, [getIframeParent, setWidth]);

  const resetStyles = useCallback(() => {
    const iframeParent = getIframeParent();
    hideIframe(() => {
      setWidth(containerRef.current, resizeRef?.current?.style?.getPropertyValue('width'));
      resizeRef.current?.style.removeProperty('width');
      containerRef.current?.classList.toggle('resizing', false);
      showIframe();
    });
    iframeParent?.style.removeProperty('pointer-events');
    document.body.style.removeProperty('cursor');
    document.body.style.removeProperty('user-select');
  }, [getIframeParent, hideIframe, setWidth, showIframe]);

  const trackResize = useDebounce(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_DRAG,
        action_name: TrackingConstant.ACTION_TOOL_SELECT,
        subaction_name: TrackingConstant.RESIZE_CANVAS,
        creative_id: CANVAS_CREATIVE_ID,
        value: window?.getComputedStyle(containerRef?.current)?.width,
      },
    });
  }, 250);

  const onMouseMove = useCallback(
    event => {
      setWidth(resizeRef.current, event.clientX - parentBoundingClientRect.current.left);
    },
    [setWidth]
  );

  const onResizeEnd = useCallback(() => {
    trackResize();
    resetStyles();

    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onResizeEndRef.current);
  }, [onMouseMove, resetStyles, trackResize]);

  useEffect(() => {
    onResizeEndRef.current = onResizeEnd;
  }, [onResizeEnd]);

  const onResizeStart = useCallback(() => {
    parentBoundingClientRect.current = containerRef.current?.getBoundingClientRect();
    applyStyles();

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onResizeEndRef.current);
  }, [applyStyles, onMouseMove]);

  const applyStylesWindowResize = useCallback(() => {
    const iframeParent = getIframeParent();
    if (!iframeParent) {
      return;
    }

    isResizingWindow.current = true;
    setWidth(iframeParent, window?.getComputedStyle(iframeParent)?.width);
  }, [getIframeParent, setWidth]);
  const resetStylesWindowResize = useDebounce(() => {
    const iframeParent = getIframeParent();
    hideIframe(() => {
      iframeParent?.style.removeProperty('width');
      showIframe();
    });
    isResizingWindow.current = false;
  }, 250);

  useEffect(() => {
    function onWindowResize() {
      if (!isResizingWindow.current) {
        applyStylesWindowResize();
      }
      resetStylesWindowResize();
    }

    window.addEventListener('resize', onWindowResize, true);

    return () => {
      window.removeEventListener('resize', onWindowResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDockClick = useCallback(() => {
    hideIframe(() => {
      if (isDocked) {
        setWidth(containerRef.current, beforeDockWidth.current);
        beforeDockWidth.current = null;
        setShowContent(true);
      } else {
        beforeDockWidth.current = containerRef.current?.style.width;
        containerRef.current.style.removeProperty('width');
        setShowContent(false);
      }

      toggleDocked();
      showIframe();
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDocked]);

  return { containerRef, resizeRef, isDocked, showContent, onDockClick, onResizeStart };
}
